import * as React from "react";

import { cn } from "@/lib/utils";
import { fluentIconComponent } from "@/components/fluent-icon-map";

/**
 * Drop-in shim for Fluent UI's `Icon` and `FontIcon`. Looks up `iconName` in
 * the lucide map and renders the matching component. Anything missing renders
 * nothing — same fail-soft behaviour the button shim uses. Honours `style`,
 * `className`, `aria-label`, `title`.
 */

// NOTE: do NOT add a `[key: string]: any` index signature here — it would
// short-circuit TypeScript's contextual typing of the explicit handlers
// below, leaving call sites with implicit-any callback parameters under
// strict mode. Explicit HTML event handlers go here instead.
export interface FluentIconProps {
  iconName?: string;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
  ariaHidden?: boolean;
  role?: string;
  tabIndex?: number;
  id?: string;
  title?: string;
  iconType?: any;
  imageProps?: any;
  styles?: any;
  componentRef?: any;
  theme?: any;
  onClick?: (ev: React.MouseEvent<SVGSVGElement>) => void;
  onMouseDown?: (ev: React.MouseEvent<SVGSVGElement>) => void;
  onMouseUp?: (ev: React.MouseEvent<SVGSVGElement>) => void;
  onMouseEnter?: (ev: React.MouseEvent<SVGSVGElement>) => void;
  onMouseLeave?: (ev: React.MouseEvent<SVGSVGElement>) => void;
  onKeyDown?: (ev: React.KeyboardEvent<SVGSVGElement>) => void;
  onError?: (ev: React.SyntheticEvent<SVGSVGElement>) => void;
  /** Pass-through for Fluent's <Icon> children — typically empty, sometimes a
   *  custom render override. We render the lucide icon and ignore children. */
  children?: React.ReactNode;
}

export const FontIcon = React.forwardRef<SVGSVGElement, FluentIconProps>(
  function FontIcon(props, ref) {
    const {
      iconName,
      className,
      style,
      ariaLabel,
      ariaHidden,
      role,
      tabIndex,
      id,
      title,
      onClick,
      onMouseDown,
      onMouseUp,
      onMouseEnter,
      onMouseLeave,
      onKeyDown,
      onError,
    } = props;

    const Component = fluentIconComponent(iconName);
    if (!Component) return null;

    // Fluent rendered an `<i>` from an icon font; the Tailwind `text-base` /
    // CSS `font-size` controlled the size. To keep that behaviour the lucide
    // svg defaults to 1em so the surrounding `style.fontSize` still drives
    // the rendered size.
    return (
      <Component
        ref={ref}
        size="1em"
        id={id}
        role={role}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
        aria-hidden={ariaHidden}
        title={title}
        onClick={onClick as any}
        onMouseDown={onMouseDown as any}
        onMouseUp={onMouseUp as any}
        onMouseEnter={onMouseEnter as any}
        onMouseLeave={onMouseLeave as any}
        onKeyDown={onKeyDown as any}
        onError={onError as any}
        className={cn(className)}
        style={{ display: "inline-block", verticalAlign: "middle", ...style }}
      />
    );
  },
);
FontIcon.displayName = "FontIcon";

/** Fluent's `Icon` is the same as `FontIcon` for our purposes. */
export const Icon = FontIcon;
